import React from 'react';
import { 
  Grid, 
  TextField, 
  InputAdornment, 
  Card, 
  CardContent, 
  Typography, 
  Box,
  Alert
} from '@mui/material';
import { Person, Email, Phone, ContactPhone, Lock } from '@mui/icons-material';
import { BandRecommendationFormData } from '../../types/BandRecommendations';
import { NavigatorFormData } from '../../types/NavigatorTypes';

type FormDataType = BandRecommendationFormData | NavigatorFormData;

interface ContactInfoProps<T extends FormDataType> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
}

const ContactInfo = <T extends FormDataType>({ formData, setFormData }: ContactInfoProps<T>) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const isNavigatorForm = (data: FormDataType): data is NavigatorFormData => {
    return 'name' in data && 'email' in data && 'phone' in data;
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContactPhone color="primary" />
            Booking Contact Information
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide the details of the person who handles bookings for this act — 
            this may be the band leader, manager, or agent. This is the contact that festival 
            programmers will reach out to if they are interested in booking the act.
          </Typography>

          <Alert 
            severity="info" 
            icon={<Lock fontSize="small" />}
            sx={{ mb: 3 }}
          >
            <strong>This information is private.</strong> It will never appear on the public band 
            page and will only be shared with verified programmers who express a booking interest. 
            Please ensure you have the contact's permission before submitting their details.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                name={isNavigatorForm(formData) ? "name" : "contactName"}
                label="Contact Name"
                placeholder="Band leader, manager, or agent"
                value={isNavigatorForm(formData) ? formData.name : formData.contactName}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                name={isNavigatorForm(formData) ? "email" : "contactEmail"}
                label="Booking Email"
                type="email"
                placeholder="Booking enquiries email address"
                value={isNavigatorForm(formData) ? formData.email : formData.contactEmail}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name={isNavigatorForm(formData) ? "phone" : "contactPhone"}
                label="Booking Phone"
                placeholder="Optional but recommended"
                value={isNavigatorForm(formData) ? formData.phone : formData.contactPhone}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ContactInfo;